import _, { find } from "lodash"

import markDiffsAsEdits from "./markDiffsAsEdits"

/**
 * Unassigned the patient from the nurse and chair
 *
 * @param {*} state
 * @param {*} action {patientId: string}
 * @returns
 */
export const deallocatePatient = stateSchema => (state, action) => {
	const patientId = action.payload
	const newState = _.cloneDeep(state)

	const patient = newState.patientSchedule.find(patient => patient.patientId === patientId)

	patient.assignedNurse = null
	patient.assignedChair = null

	const validatedNewState = stateSchema.parse(newState)

	return validatedNewState
}

/**
 * Transfers patients from one nurse to another
 *
 * @param {*} state
 * @param {*} action {newNurseId: string, patientId: string chairId: string}[]
 * @returns
 */
export const transferPatientsBetweenNursesAndChairs = stateSchema => (state, action) => {
	const patientTransfers = action.payload

	const newState = _.cloneDeep(state)

	for (const transfer of patientTransfers) {
		const { newNurseId, newChairId, patientId } = transfer
		const patientInNewState = newState.patientSchedule.find(
			patient => patient.patientId === patientId
		)

		if (newNurseId && newNurseId !== patientInNewState.assignedNurse) {
			patientInNewState.assignedNurse = newNurseId
		}

		if (newChairId && newChairId !== patientInNewState.assignedChair) {
			patientInNewState.assignedChair = newChairId
		}
	}

	const sliceName = action.type.split("/")[0]

	if (sliceName !== "sandbox") {
		markDiffsAsEdits(state, newState)
	}

	const validatedNewState = stateSchema.parse(newState)
	return validatedNewState
}

/**
 * Mark a nurse as edited
 *
 * type EditedProperty =
 *   | "name"
 *   | "scheduledStartTime"
 *   | "scheduledEndTime"
 *   | "scheduledLunchStartTime"
 *   | "scheduledLunchEndTime"
 *
 * @param {Object} state - The current state of the schedule

 * @param {Object} action - The action object {nurseId: string, editedProperties: EditedProperty[]}
 * @returns {Object} The updated state with the nurse marked as edited
 */
export const markNurseAsEdited = stateSchema => (state, action) => {
	const { nurseId, editedProperties } = action.payload
	const newState = _.cloneDeep(state)
	newState.editedNurses.push({
		nurseId,
		added: false,
		deleted: false,
		name: editedProperties.includes("name"),
		scheduledStartTime: editedProperties.includes("scheduledStartTime"),
		scheduledEndTime: editedProperties.includes("scheduledEndTime"),
		scheduledLunchStartTime: editedProperties.includes("scheduledLunchStartTime"),
		scheduledLunchEndTime: editedProperties.includes("scheduledLunchEndTime")
	})

	const validatedNewState = stateSchema.parse(newState)
	return validatedNewState
}

/**
 * Mark a patient as edited
 *
 * type EditedProperty =
 *   | "assignedNurse"
 *   | "assignedChair"
 *   | "acuity"
 *
 * @param {Object} state - The current state of the schedule
 * @param {Object} action - The action object {patientId: number, editedProperties: EditedProperty[]}
 * @returns {Object} The updated state with the patient marked as edited
 */
export const markPatientAsEdited = stateSchema => (state, action) => {
	const { patientId, editedProperties } = action.payload
	const newState = _.cloneDeep(state)
	newState.editedPatients.push({
		patientId,
		assignedNurse: editedProperties.includes("assignedNurse"),
		assignedChair: editedProperties.includes("assignedChair"),
		acuity: editedProperties.includes("acuity"),
		pendingChanges: editedProperties.includes("pendingChanges"),
		notes: editedProperties.includes("notes"),
		cancellationConsideredForReassignment: editedProperties.includes(
			"cancellationConsideredForReassignment"
		),
		shownCancelledPatient: editedProperties.includes("shownCancelledPatient")
	})

	const validatedNewState = stateSchema.parse(newState)
	return validatedNewState
}

export const assignPatient = stateSchema => (state, action) => {
	const { patientId, nurseId, chairId } = action.payload
	const newState = _.cloneDeep(state)

	const patient = find(newState.patientSchedule, { patientId })
	if (!patient) {
		throw new Error("Patient not found")
	}

	patient.assignedNurse = nurseId
	patient.assignedChair = chairId

	const nurse = find(newState.nurseSchedule, { nurseId })
	if (!nurse) {
		throw new Error("Nurse not found")
	}

	const chair = find(newState.chairSchedule, { chairId })
	if (!chair) {
		throw new Error("Chair not found")
	}

	const existingEditedPatient = find(newState.editedPatients, { patientId })
	if (existingEditedPatient) {
		existingEditedPatient.assignedNurse = true
		existingEditedPatient.assignedChair = true
	} else {
		newState.editedPatients.push({
			patientId,
			assignedNurse: true,
			assignedChair: true,
			acuity: false,
			pendingChanges: false,
			notes: false,
			cancellationConsideredForReassignment: false,
			shownCancelledPatient: false
		})
	}

	const existingEditedNurse = find(newState.editedNurses, { nurseId })
	if (existingEditedNurse) {
		existingEditedNurse.added = true
	} else {
		newState.editedNurses.push({
			nurseId,
			added: true,
			deleted: false,
			name: false,
			scheduledStartTime: false,
			scheduledEndTime: false,
			scheduledLunchStartTime: false,
			scheduledLunchEndTime: false
		})
	}

	const validatedNewState = stateSchema.parse(newState)
	return validatedNewState
}


/**
 * Resets all edits for patients and nurses to false
 * @param {*} state
 * @returns {Object} The updated state with all edits reset to false
 */
export const resetAllEdits = stateSchema => state => {
	const newState = _.cloneDeep(state)

	newState.editedPatients = newState.patientSchedule.map(patient => ({
		patientId: patient.patientId,
		acuity: false,
		assignedNurse: false,
		assignedChair: false,
		pendingChanges: false,
		notes: false,
		cancellationConsideredForReassignment: false,
		shownCancelledPatient: false
	}))

	newState.editedNurses = newState.nurseSchedule.map(nurse => ({
		nurseId: nurse.nurseId,
		added: false,
		deleted: false,
		name: false,
		scheduledLunchStartTime: false,
		scheduledLunchEndTime: false,
		scheduledStartTime: false,
		scheduledEndTime: false
	}))

	const validatedNewState = stateSchema.parse(newState)
	return validatedNewState
}

/**
 * Sets the consideredCancellationForReassignments flag for the given patients
 * @param {*} state
 * @param {*} action {patientIds: number[]}
 * @returns
 */

export const setPatientsCancellationConsideredForReassignment = stateSchema => (state, action) => {
	const { patientIds } = action.payload
	const newState = _.cloneDeep(state)

	for (let patientObject of newState.patientSchedule) {
		if (!patientIds.includes(patientObject.patientId)) {
			continue
		}
		patientObject.cancellationConsideredForReassignment = true
		
		// Update the corresponding entry in editedPatients
		const editedPatientIndex = newState.editedPatients.findIndex(
			patient => patient.patientId === patientObject.patientId
		)
		
		if (editedPatientIndex !== -1) {
			// Update existing entry
			newState.editedPatients[editedPatientIndex].cancellationConsideredForReassignment = true
		} else {
			// Add new entry to editedPatients
			newState.editedPatients.push({
				patientId: patientObject.patientId,
				acuity: false,
				assignedNurse: false,
				assignedChair: false,
				pendingChanges: false,
				notes: false,
				cancellationConsideredForReassignment: true,
				shownCancelledPatient: false
			})
		}
	}

	const validatedNewState = stateSchema.parse(newState)
	return validatedNewState
}

/**
 * Sets the shownCancelledPatient flag for the given patients
 * @param {*} state
 * @param {*} action {patientIds: number[]}
 * @returns
 */
export const setShownCancelledPatients = stateSchema => (state, action) => {
	const { patientIds } = action.payload
	const newState = _.cloneDeep(state)

	for (let patientObject of newState.patientSchedule) {
		if (!patientIds.includes(patientObject.patientId)) {
			continue
		}
		patientObject.shownCancelledPatient = true
		
		// Update the corresponding entry in editedPatients
		const editedPatientIndex = newState.editedPatients.findIndex(
			patient => patient.patientId === patientObject.patientId
		)
		
		if (editedPatientIndex !== -1) {
			// Update existing entry
			newState.editedPatients[editedPatientIndex].shownCancelledPatient = true
		} else {
			// Add new entry to editedPatients
			newState.editedPatients.push({
				patientId: patientObject.patientId,
				acuity: false,
				assignedNurse: false,
				assignedChair: false,
				pendingChanges: false,
				notes: false,
				cancellationConsideredForReassignment: false,
				shownCancelledPatient: true
			})
		}
	}

	const validatedNewState = stateSchema.parse(newState)
	return validatedNewState
}
